/*
 * Al Amin Opticals — sleek mobile offer adapter, v3 (2026-10-09).
 * Presentation only: reuses the app's prices, options, selected state and CTA.
 * Does not submit forms, calculate prices, move nodes or intercept app clicks.
 * Supports native buttons, ARIA radio tiles, custom tags and open shadow roots.
 */
(() => {
  'use strict';
  if (window.__aaOfferDisplayFixes || !document.body.classList.contains('template-product')) return;
  window.__aaOfferDisplayFixes = true;

  const ownScript = document.currentScript;
  const cssUrl = ownScript?.dataset.aaOfferStyles ||
    document.querySelector('link[href*="aa-promo-display-fixes.css"]')?.href;
  const mobile = window.matchMedia('(max-width: 767px)');
  const headingPattern = /^buy\s+more\s*[,\-–—]?\s*save\s+more$/i;
  const onePattern = /\b1\s*item\b/i;
  const twoPattern = /\b2\s*items\b/i;
  const addPattern = /^add\s+[12]\s+items?\b/i;
  const moneyPattern = /(?:dhs\.?|aed|د\.?إ|\$|€|£)\s*[\d,]+(?:\.\d+)?/i;
  const textElements = 'div,span,p,strong,b,small,h2,h3,h4,h5,h6,legend,em,[role="heading"]';
  const excluded = '[data-aa-opening-promo],.aa-opening-promo-pill,nav,footer,[data-lens-customizer]';
  const roots = new Set();
  const models = new WeakMap();
  const scopes = new Map();
  const registeredHosts = new WeakSet();
  const flowElements = new Set();
  const stylesInstalled = new WeakSet();
  let timer = 0;

  const text = el => String(el instanceof HTMLInputElement ? el.value :
    (el.innerText || el.textContent || '')).replace(/\s+/g, ' ').trim();
  const mark = (el, part) => { if (el) el.setAttribute('data-aa-offer-part', part); };
  const matchingText = (parent, predicate) => Array.from(parent.querySelectorAll(textElements))
    .filter(el => predicate(text(el)) && !Array.from(el.children).some(child => predicate(text(child))));
  const composedParent = node => node.parentElement || node.getRootNode()?.host || null;
  const composedClosest = (node, selector) => {
    for (let el = node; el; el = composedParent(el)) if (el.matches?.(selector)) return el;
    return null;
  };
  const clear = root => {
    root.removeAttribute('data-aa-offer-mobile');
    root.querySelectorAll('[data-aa-offer-part],[data-aa-offer-selected],[data-aa-offer-label-row]')
      .forEach(el => {
        el.removeAttribute('data-aa-offer-part');
        el.removeAttribute('data-aa-offer-selected');
        el.removeAttribute('data-aa-offer-label-row');
      });
    const previous = models.get(root);
    if (previous?.externalCta) previous.cta?.removeAttribute('data-aa-offer-external-cta');
  };

  // The first matching sibling pair is the complete tile pair, not its leaves.
  const findModel = root => {
    if (root.querySelector('h1,variant-picker,[data-product-image],[data-lens-customizer]')) return null;
    const all = Array.from(root.querySelectorAll('*'));
    const ctas = all.filter(el => el.matches('button,input[type="submit"],[role="button"]') && addPattern.test(text(el)));
    let cta = ctas.find(el => el.getClientRects().length) || ctas[0] || null;
    const card = (own, other) => all.find(el => {
      if (el.matches('input,script,style,svg,path,option,select')) return false;
      const content = text(el);
      return el !== cta && !el.contains(cta) && content.length < 600 &&
        own.test(content) && !other.test(content) && el.parentElement &&
        other.test(text(el.parentElement));
    });
    const one = card(onePattern, twoPattern);
    const two = card(twoPattern, onePattern);
    if (!one || !two || one.parentElement !== two.parentElement) return null;
    const grid = one.parentElement;
    if (cta && grid.contains(cta)) return null;
    const knownHost = root.matches('hype-volume-tiles') || !!root.closest('hype-volume-tiles') ||
      root.getRootNode()?.host?.matches('hype-volume-tiles');
    // For unknown markup require the complete heading + choices + purchase CTA.
    if (!cta && !knownHost) return null;
    let externalCta = false;
    // Some app versions place their button beside the custom element, not inside.
    if (!cta && knownHost) {
      for (let parent = composedParent(root), depth = 0; parent && depth < 3; parent = composedParent(parent), depth++) {
        if (parent.querySelector('h1,variant-picker,[data-product-image]')) break;
        const nearby = Array.from(parent.querySelectorAll('button,input[type="submit"],[role="button"]'))
          .filter(el => addPattern.test(text(el)) && !grid.contains(el));
        if (nearby.length === 1) { cta = nearby[0]; externalCta = true; break; }
      }
    }
    return { root, cta, externalCta, one, two, grid };
  };

  const selected = card => {
    const scope = card.getRootNode();
    const radio = card.matches('label') && card.htmlFor
      ? scope.getElementById?.(card.htmlFor) : card.querySelector('input[type="radio"]');
    if (radio instanceof HTMLInputElement) return radio.checked;
    for (const key of ['aria-checked','aria-pressed','data-selected','data-active']) {
      if (card.hasAttribute(key)) return card.getAttribute(key) === 'true';
    }
    if (/(?:^|[\s_-])(?:selected|active|checked)(?:$|[\s_-])/i.test(String(card.className || ''))) return true;
    const style = getComputedStyle(card);
    if (parseFloat(style.borderTopWidth) <= 0) return false;
    const rgba = style.borderTopColor.match(/[\d.]+/g);
    if (!rgba || rgba.length < 3 || (rgba.length > 3 && +rgba[3] === 0)) return false;
    const rgb = rgba.slice(0,3).map(Number);
    return Math.max(...rgb) - Math.min(...rgb) > 45;
  };

  const installShadowStyles = root => {
    const tree = root.getRootNode();
    if (!(tree instanceof ShadowRoot) || stylesInstalled.has(tree) || !cssUrl) return;
    stylesInstalled.add(tree);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = cssUrl;
    link.setAttribute('data-aa-offer-internal', 'styles');
    tree.appendChild(link);
  };

  // Collapse only neutral wrappers consisting of the numeric quantity and unit.
  // Keep native/ARIA radio controls and all event targets in their original place.
  const labelRow = (card, quantities, units) => {
    if (quantities.length !== 1 || units.length !== 1) return;
    const quantity = quantities[0];
    const unit = units[0];
    let group = quantity.parentElement;
    while (group && group !== card && !group.contains(unit)) group = group.parentElement;
    if (!group) return;
    if (group === card) {
      if (quantity.parentElement === card && unit.parentElement === card) {
        card.setAttribute('data-aa-offer-label-row', 'direct');
      }
    } else if (/^[12]\s*items?$/i.test(text(group))) {
      mark(group, 'label-group');
      Array.from(group.querySelectorAll('*')).forEach(el => {
        if (el !== quantity && el !== unit && (el.contains(quantity) || el.contains(unit))) mark(el, 'label-wrapper');
      });
    }
  };

  const enhance = root => {
    clear(root);
    if (!mobile.matches || !root.isConnected || !root.getClientRects().length) return false;
    let model = findModel(root);
    if (!model) {
      const cached = models.get(root);
      if (cached && [cached.one,cached.two,cached.grid].every(el => root.contains(el)) &&
          (!cached.cta || cached.cta.isConnected) &&
          cached.one.parentElement === cached.grid && cached.two.parentElement === cached.grid) model = cached;
    }
    if (!model) return false;
    // Preserve the same app button during transient Adding… / Sold out labels.
    const previous = models.get(root);
    if (!model.cta && previous?.cta?.isConnected &&
        model.one === previous.one && model.two === previous.two &&
        (root.contains(previous.cta) || previous.externalCta)) {
      model.cta = previous.cta;
      model.externalCta = previous.externalCta;
    }
    models.set(root, model);
    const {one,two,grid,cta,externalCta} = model;
    const state = [selected(one),selected(two)];
    if (state[0] === state[1] && cta) {
      const count = text(cta).match(/^add\s+([12])\s+items?\b/i)?.[1];
      if (count) { state[0] = count === '1'; state[1] = count === '2'; }
    }
    const heading = matchingText(root, value => headingPattern.test(value))[0];
    const title = matchingText(root, value => /^double your style\b/i.test(value) && value.length < 90)[0];
    mark(heading,'eyebrow'); mark(title,'title');
    if (heading && title) {
      let header = heading.parentElement;
      while (header && header !== root && !header.contains(title)) header = header.parentElement;
      if (header && header !== root && !header.contains(grid) && (!cta || !header.contains(cta))) mark(header,'header');
    }
    mark(grid,'grid');
    [one,two].forEach((card,index) => {
      mark(card,'card');
      card.setAttribute('data-aa-offer-selected',String(state[index]));
      const quantities = matchingText(card,value => /^[12]$/.test(value));
      const units = matchingText(card,value => /^items?$/i.test(value));
      quantities.forEach(el => mark(el,'quantity'));
      units.forEach(el => mark(el,'unit'));
      matchingText(card,value => /^[12]\s*items?$/i.test(value))
        .filter(el => !el.contains(quantities[0]) && !el.contains(units[0])).forEach(el => mark(el,'label'));
      matchingText(card,value => moneyPattern.test(value) && value.length < 80).forEach(el => mark(el,'price'));
      matchingText(card,value => /^save\s+\d+(?:\.\d+)?\s*%$/i.test(value)).forEach(el => mark(el,'badge'));
      labelRow(card,quantities,units);
    });
    if (cta) {
      if (externalCta) cta.setAttribute('data-aa-offer-external-cta','true');
      else {
        mark(cta,'cta');
        if (cta.parentElement !== root && !cta.parentElement.contains(grid)) mark(cta.parentElement,'footer');
      }
    }
    // Find the totals separator even when selecting 1 item hides the unit price.
    const perItem = matchingText(root,value => /\/\s*item\b/i.test(value) && value.length < 90)[0];
    const total = matchingText(root,value => moneyPattern.test(value) && value.length < 160)
      .find(el => !grid.contains(el) && (!cta || !cta.contains(el)));
    let summary = perItem || total;
    if (summary && !grid.contains(summary) && (!cta || !cta.contains(summary))) {
      while (summary.parentElement && summary.parentElement !== root &&
          !summary.parentElement.contains(grid) && (!cta || !summary.parentElement.contains(cta))) summary = summary.parentElement;
      mark(summary,'summary');
    }
    installShadowStyles(root);
    root.setAttribute('data-aa-offer-mobile','true');
    return true;
  };

  const discover = scope => {
    if (!mobile.matches) return;
    scope.querySelectorAll('hype-volume-tiles').forEach(host => {
      if (!composedClosest(host,excluded) && findModel(host)) roots.add(host);
    });
    matchingText(scope,value => value.length < 45 && headingPattern.test(value)).forEach(heading => {
      if (composedClosest(heading,excluded)) return;
      if (Array.from(roots).some(root => root.contains(heading))) return;
      let candidate = heading.parentElement;
      for (let depth=0; candidate && candidate !== scope && depth<10; depth++,candidate=candidate.parentElement) {
        if (text(candidate).length > 3500 || candidate.matches('body,main')) break;
        if (roots.has(candidate)) break;
        if (candidate.matches('form,product-form')) continue;
        const model = findModel(candidate);
        if (model) { roots.add(candidate); models.set(candidate,model); break; }
      }
    });
  };

  const updateFlow = active => {
    const next = new Set();
    active.forEach(root => {
      const bar = composedClosest(root,'.js-qb-buy-buttons');
      const layout = bar?.closest('.product-rayban-layout');
      if (bar && layout) { next.add(bar); next.add(layout); }
    });
    flowElements.forEach(el => {
      if (!next.has(el)) { el.removeAttribute('data-aa-offer-flow'); flowElements.delete(el); }
    });
    next.forEach(el => {
      if (!flowElements.has(el)) { el.setAttribute('data-aa-offer-flow','true'); flowElements.add(el); }
    });
  };
  const refresh = () => {
    timer = 0;
    roots.forEach(root => { if (!root.isConnected) { clear(root); roots.delete(root); } });
    if (mobile.matches) scopes.forEach((observer,scope) => discover(scope));
    const active=[];
    roots.forEach(root => { if (enhance(root)) active.push(root); });
    updateFlow(active);
  };
  const schedule = () => { if (!timer) timer=window.setTimeout(refresh,80); };
  const offerText = node => /buy\s+more[\s\S]{0,30}save\s+more/i.test(node.textContent || '');
  const internal = node => node.nodeType === 1 && node.hasAttribute('data-aa-offer-internal');

  const inspectHosts = container => {
    const nodes = [container,...(container.querySelectorAll?.('*') || [])];
    nodes.forEach(el => {
      if (el.nodeType !== 1 || composedClosest(el,excluded)) return;
      if (el.shadowRoot) watch(el.shadowRoot);
      if (el.localName?.includes('-') && !registeredHosts.has(el)) {
        registeredHosts.add(el);
        customElements.whenDefined(el.localName).then(() => {
          if (el.isConnected && el.shadowRoot) { watch(el.shadowRoot); schedule(); }
        });
      }
    });
  };
  const watch = scope => {
    if (scopes.has(scope)) return;
    const observer = new MutationObserver(records => {
      let relevant = false;
      for (const record of records) {
        if (record.type === 'childList') Array.from(record.addedNodes).forEach(node => {
          if (node.nodeType === 1 && !internal(node)) {
            inspectHosts(node);
            if (offerText(node) || node.matches('hype-volume-tiles')) relevant=true;
          }
        });
        if (Array.from(roots).some(root => root.contains(record.target) ||
            Array.from(record.removedNodes).some(node => node === root || node.contains?.(root)))) relevant=true;
        if (record.type === 'attributes' && offerText(record.target) && (record.target.textContent || '').length < 3500) relevant=true;
      }
      if (relevant && mobile.matches) schedule();
    });
    scopes.set(scope,observer);
    observer.observe(scope,{
      childList:true,subtree:true,characterData:true,attributes:true,
      attributeFilter:['class','style','hidden','aria-checked','aria-pressed','checked','data-selected','data-active']
    });
    scope.addEventListener('change',schedule,{passive:true});
    scope.addEventListener('click',event => {
      if (Array.from(roots).some(root => root.contains(event.target))) schedule();
    },{passive:true});
    inspectHosts(scope);
  };
  const start = () => {
    // Some apps mount their offer outside MainContent. Detection is still guarded.
    watch(document.body);
    refresh();
    document.addEventListener('shopify:section:load',() => { inspectHosts(document.body); schedule(); });
    window.addEventListener('pageshow',schedule);
    const resize = () => { if (mobile.matches) inspectHosts(document.body); schedule(); };
    if (mobile.addEventListener) mobile.addEventListener('change',resize); else mobile.addListener(resize);
    // Catch late custom-element upgrades that attach a root to an existing host.
    [500,1500,4000,10000].forEach(delay => window.setTimeout(() => {
      if (mobile.matches) { inspectHosts(document.body); schedule(); }
    },delay));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',start,{once:true});
  else start();
})();
